import { useCallback, useEffect } from 'react';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { venomHistory } from 'client/utils/history/venom-history';
import { getCreateListingUrl } from 'site-modules/shared/utils/private-party/private-party';
import { fireAppraisalPixel, fireSelectVinPixel } from 'site-modules/shared/utils/appraisal/common-utils';
import { APPRAISE_BY_VIN_TAB_EVENT_DATA } from 'site-modules/shared/components/appraisal/appraisal-tabs/constants';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import { VIN_TAB } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { useAppraisalTabsState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';

export function useVinSubmit({
  creativeId,
  isVinValid,
  vinInputValue,
  setWasFormSubmitted,
  setIsSubmitting,
  decodeVin,
  setStylesAndVin,
  isWIDG1125chal1,
  isSellInCityState,
  isPrivatePartyBtn,
  isAppraiserPageOpened,
}) {
  const { setVin, setStyleIds, setMmy, setColors } = useAppraisalTabsState();

  const resetIsSubmitting = useCallback(() => {
    setIsSubmitting(false);
  }, [setIsSubmitting]);

  const fireVinInputErrorTracking = useCallback(
    (subactionName, value) => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          ...APPRAISE_BY_VIN_TAB_EVENT_DATA,
          creative_id: creativeId,
          subaction_name: subactionName,
          value,
        },
      });

      const vinErrorValue = `${vinInputValue}_${subactionName}_${value}`;
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          ...APPRAISE_BY_VIN_TAB_EVENT_DATA,
          creative_id: creativeId,
          subaction_name: TrackingConstant.SUBACTION_VIN_ERROR,
          value: vinErrorValue,
        },
      });
    },
    [creativeId, vinInputValue]
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      setMmy({});
      setStyleIds(null);
      setVin('');

      setWasFormSubmitted(true);
      setIsSubmitting(true);

      if (!isVinValid) {
        fireVinInputErrorTracking(TrackingConstant.SUBACTION_VIN_VALIDATION, TrackingConstant.VIN_FORMAT_INVALID);
        resetIsSubmitting();
        return;
      }

      const stylesResponse = await decodeVin(vinInputValue);
      const styles = stylesResponse?.results || [];

      if (isWIDG1125chal1 && isSellInCityState && !isPrivatePartyBtn) {
        venomHistory.push(`/sell-car/instant-cash-offer/?icoVin=${vinInputValue}`);
        return;
      }

      const styleIds = styles.map(({ styleId }) => styleId);

      if (styleIds.length) {
        fireSelectVinPixel(vinInputValue, creativeId);
      }

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
        event_data: {
          ...APPRAISE_BY_VIN_TAB_EVENT_DATA,
          creative_id: creativeId,
          action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
          subaction_name: TrackingConstant.SUBMIT_VEHICLE,
          value: TrackingConstant.VIN,
        },
      });

      if (styleIds.length) {
        fireAppraisalPixel('VIN', creativeId);
      }

      if (isPrivatePartyBtn) {
        EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.START_PP_LISTING, {
          value: VIN_TAB.label,
          creativeId,
        });

        venomHistory.push(getCreateListingUrl(vinInputValue));
        return;
      }

      setColors({ exteriorColor: stylesResponse?.exteriorColor, interiorColor: stylesResponse?.interiorColor });
      setVin(vinInputValue);
      setStyleIds(styleIds);
      setStylesAndVin(styles, vinInputValue);
    },
    [
      creativeId,
      decodeVin,
      fireVinInputErrorTracking,
      isPrivatePartyBtn,
      isSellInCityState,
      isVinValid,
      isWIDG1125chal1,
      resetIsSubmitting,
      setColors,
      setIsSubmitting,
      setMmy,
      setStyleIds,
      setStylesAndVin,
      setVin,
      setWasFormSubmitted,
      vinInputValue,
    ]
  );

  useEffect(() => {
    if (isAppraiserPageOpened) {
      resetIsSubmitting();
    }
  }, [isAppraiserPageOpened, resetIsSubmitting]);

  useEffect(() => {
    const onPageShow = ({ persisted }) => {
      if (persisted) {
        resetIsSubmitting();
        setWasFormSubmitted(false);
      }
    };

    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [resetIsSubmitting, setWasFormSubmitted]);

  return {
    onSubmit,
    resetIsSubmitting,
  };
}
